// Per-sensor trend modal for the Barn Monitor tiles.
//
// Deliberately a mirror of ForecastDetailModal in components/pages/Dashboard.js
// -- same shell, same .forecastGrid / .kpiRow / .forecastSide layout, same
// InsightCards rail -- because farmers have already learned that pattern from
// the Weight and FCR tiles. It lives in its own file rather than inside
// Dashboard.js (which is ~2000 lines, ~400 of them the forecast modal) for the
// same reason FeedAnalysisModal does.
//
// Unlike FeedAnalysisModal this fetches nothing: the dashboard poll already has
// every reading, so everything arrives as props and the modal cannot show
// numbers that disagree with the tile behind it.

import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import MaterialIcon from 'material-icons-react';
import { useTranslation } from 'react-i18next';
import { SensorTrendChart, InsightCards } from './chartPrimitives';
import { buildSensorInsights } from './sensorInsights';

const CHART_HEIGHT = 400;

// Matches .sensorTile's border-top colours in App.css so the modal reads as the
// same object the user clicked.
const STATUS_COLOR = {
  ok: '#28a745',
  high: '#c82333',
  low: '#0d6efd',
  idle: '#9aa0c0',
};

export default function SensorTrendModal({
  show, onClose, sensor, status = 'idle',
  values = [], xLabels = [], timestamps = [],
  now, dayMin, dayMax, dayAvg, lo = null, hi = null, lastUpdated,
}) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  // ECharts measures its container on mount. Mounted while the dialog is still
  // animating it reads the wrong width and never recovers on its own --
  // echarts-for-react swallows the first size-sensor callback. Wait for
  // onEntered, when the dialog has its final dimensions.
  const [entered, setEntered] = useState(false);

  if (!sensor) return null;

  const i18nNum = (v, dp) => {
    const nn = Number(v);
    if (!Number.isFinite(nn)) return '—';
    try {
      return new Intl.NumberFormat(i18n.language || 'en', {
        minimumFractionDigits: dp, maximumFractionDigits: dp,
      }).format(nn);
    } catch (e) { return nn.toFixed(dp); }
  };

  const unit = sensor.unit || '';
  const dp = sensor.decimals != null ? sensor.decimals : 1;
  const label = t(sensor.labelKey);
  const finite = (values || []).filter((v) => Number.isFinite(v));
  const hasData = finite.length > 0;

  const statusLabel = {
    ok: t('dashboard:sensors.status.inRange'),
    high: t('dashboard:status.high'),
    low: t('dashboard:status.low'),
    idle: t('dashboard:sensors.status.noData'),
  }[status] || t('dashboard:sensors.status.noData');

  const bandText = lo !== null && hi !== null
    ? `${i18nNum(lo, dp)}–${i18nNum(hi, dp)}${unit}`
    : hi !== null
      ? t('dashboard:sensors.insights.below', { value: `${i18nNum(hi, dp)}${unit}` })
      : lo !== null
        ? t('dashboard:sensors.insights.above', { value: `${i18nNum(lo, dp)}${unit}` })
        : null;

  const cards = hasData
    ? buildSensorInsights({
        t, values: finite, timestamps, lo, hi, unit,
        decimals: dp, deadband: sensor.deadband, num: i18nNum,
      })
    : [];

  const kpi = (labelText, value, accent) => (
    <div className={`kpiCard${accent ? ' kpiCardAccent' : ''}`}>
      <div className="kpiLabel">{labelText}</div>
      <div className={`kpiValue${accent ? ' kpiValueAccent' : ''}`}>
        {Number.isFinite(Number(value)) ? i18nNum(value, dp) : '—'}
        {unit ? <span className="unit">{unit}</span> : null}
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      dialogClassName="forecastModalWide"
      onEntered={() => setEntered(true)}
      onExited={() => setEntered(false)}
    >
      <Modal.Header>
        <div className="forecastModalHeader">
          <Modal.Title>{label}</Modal.Title>
          <span className={`sensorChip ${status}`}>
            <MaterialIcon icon="fiber_manual_record" color="#fff" size={12} />
            {statusLabel}
          </span>
        </div>
        <button type="button" className="modalCloseBtn" onClick={onClose}
                aria-label={t('common:actions.close')}>×</button>
      </Modal.Header>

      <Modal.Body>
        <div className="forecastSubtitle">
          {t('dashboard:panel.todaysTrends')}
          {hasData ? ` · ${t('dashboard:sensors.readingsToday', { n: finite.length })}` : ''}
          {lastUpdated && lastUpdated !== 'Invalid date'
            ? ` · ${t('dashboard:status.updated')} ${lastUpdated}` : ''}
          {bandText && (
            <span className="forecastBreedTag">
              {t('dashboard:sensors.targetRange')} {bandText}
            </span>
          )}
        </div>

        {!hasData ? (
          <div className="noDataState">
            <MaterialIcon icon="query_stats" color="#c5c8df" size={56} />
            <div className="noDataText">{t('dashboard:empty.noDataAvailable')}</div>
            <div className="noDataHint">{t('dashboard:empty.waitingForReadings')}</div>
          </div>
        ) : (
          <div className="forecastGrid">
            <div className="forecastMain">
              <div className="kpiRow kpiRow4">
                {kpi(t('common:sensors.now'), now, true)}
                {kpi(t('common:metrics.min'), dayMin)}
                {kpi(t('common:metrics.max'), dayMax)}
                {kpi(t('common:metrics.avg'), dayAvg)}
              </div>
              {/* .forecastChartBig is not strictly required (the chart renders
                  SVG, so App.css:43 cannot reach it) but it is free, it carries
                  the small-screen height rule, and it is the safety net if
                  anyone ever drops the svg renderer. */}
              <div className="forecastChartBig">
                {entered ? (
                  <SensorTrendChart
                    xLabels={xLabels}
                    values={values}
                    lo={lo}
                    hi={hi}
                    color={STATUS_COLOR[status] || STATUS_COLOR.idle}
                    unit={unit}
                    decimals={dp}
                    minSpan={sensor.minSpan}
                    height={CHART_HEIGHT}
                  />
                ) : (
                  <div className="chartMountPlaceholder" style={{ height: CHART_HEIGHT }} />
                )}
              </div>
            </div>
            <aside className="forecastSide">
              <div className="sideHeading">{t('dashboard:fcr.whatThisMeans')}</div>
              <InsightCards cards={cards} />
            </aside>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="modalCancelBtn" onClick={onClose}>
          {t('common:actions.close')}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
